import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Trophy, Award, Medal, Sparkles, Gift, Shirt, Star, ArrowUpRight, ArrowRight, Check } from 'lucide-react';

const ProductsPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const categories = [
    {
      id: 'trophies',
      name: 'Trophies',
      icon: Trophy,
      description: 'Sports trophies, corporate awards, fantasy league prizes, and more in every size and style imaginable.',
      image: 'https://images.unsplash.com/photo-1578269174936-2709b6aeb913?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
      items: ['Sports Trophies', 'Corporate Awards', 'Fantasy League', 'Spirit Awards', 'School Awards'],
      startingPrice: 15
    },
    {
      id: 'plaques',
      name: 'Plaques',
      icon: Award,
      description: 'Wood, acrylic, and glass plaques perfect for employee recognition, memorials, and achievement awards.',
      image: 'https://images.unsplash.com/photo-1576723420195-6ac9d4c71fe8?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
      items: ['Wood Plaques', 'Acrylic Plaques', 'Photo Plaques', 'Perpetual Plaques', 'Name Plates'],
      startingPrice: 25
    },
    {
      id: 'crystal',
      name: 'Crystal Awards',
      icon: Sparkles,
      description: 'Elegant crystal and glass awards for executive recognition, milestone celebrations, and premium gifts.',
      image: 'https://images.unsplash.com/photo-1642104744809-14b986179927?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
      items: ['Crystal Trophies', 'Glass Awards', '3D Engraving', 'LED Bases', 'Gift Boxes'],
      startingPrice: 45
    },
    {
      id: 'medals',
      name: 'Medals & Ribbons',
      icon: Medal,
      description: 'Custom medals for sports events, academic achievements, and corporate recognition programs.',
      image: 'https://images.unsplash.com/photo-1706374503312-7a4a4c030d2d?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
      items: ['Sports Medals', 'Academic Medals', 'Custom Ribbons', 'Neck Drapes', 'Display Cases'],
      startingPrice: 5
    },
    {
      id: 'promotional',
      name: 'Promotional Products',
      icon: Gift,
      description: 'Business gifts, trade show giveaways, and branded merchandise for every occasion.',
      image: 'https://images.unsplash.com/photo-1637635753380-20bf6f46ede0?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
      items: ['Pens & Writing', 'Drinkware', 'Tech Accessories', 'Bags & Totes', 'Office Items'],
      startingPrice: 2
    },
    {
      id: 'apparel',
      name: 'Custom Apparel',
      icon: Shirt,
      description: 'Custom t-shirts, embroidered polos, uniforms, and branded apparel for teams and businesses.',
      image: 'https://images.unsplash.com/photo-1759688983881-0742f416a4b4?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
      items: ['T-Shirts', 'Polos', 'Hats & Caps', 'Jackets', 'Uniforms'],
      startingPrice: 15
    }
  ];

  const features = [
    { title: 'Custom Engraving', desc: 'Precision laser engraving' },
    { title: 'Free Design Help', desc: 'Our team assists with layout' },
    { title: 'Quality Inspection', desc: 'Every item checked' },
    { title: 'Fast Turnaround', desc: '4-day standard production' }
  ];

  return (
    <div data-testid="products-page" className="min-h-screen pt-28 pb-20 bg-white">
      {/* Hero Section */}
      <section className="max-w-6xl mx-auto px-6 py-16 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-xs uppercase tracking-widest text-[#D4AF37] mb-3 font-semibold">San Diego Trophy & Award Products</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl text-[#1a365d] mb-4 font-bold">Custom Trophies, Plaques & Awards in San Diego</h1>
          <p className="text-base text-gray-600 max-w-3xl mx-auto mb-4">
            From championship trophies to corporate plaques, RC Awards has the perfect recognition solution for every occasion. We stock thousands of products and offer complete customization with <Link to="/services/engraving" className="text-[#D4AF37] hover:underline">laser engraving</Link>, <Link to="/services/embroidery" className="text-[#D4AF37] hover:underline">embroidery</Link>, and <Link to="/services/screen-printing" className="text-[#D4AF37] hover:underline">screen printing</Link>.
          </p>
          <p className="text-sm text-gray-500 max-w-2xl mx-auto">
            Quality craftsmanship since 1995. 4-day standard turnaround with same-day rush available. Best prices guaranteed in San Diego County.
          </p>
        </motion.div>
      </section>

      {/* Quick Shop Banner */}
      <section className="max-w-6xl mx-auto px-6 mb-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="bg-gradient-to-r from-[#1a365d] to-[#2d4a7c] rounded-2xl p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6"
        >
          <div className="text-center md:text-left">
            <h2 className="text-2xl font-bold text-white mb-2">Shop Our Full Selection Online</h2>
            <p className="text-white/70">Browse thousands of products with real-time pricing on our e-commerce store.</p>
          </div>
          <a
            href="https://www.rcawards.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-[#D4AF37] text-[#1a365d] px-6 py-3 rounded-full text-sm font-semibold hover:bg-[#c9a432] transition-colors inline-flex items-center gap-2 flex-shrink-0"
          >
            Shop Now
            <ArrowUpRight size={16} />
          </a>
        </motion.div>
      </section>

      {/* Product Categories */}
      <section className="max-w-6xl mx-auto px-6 pb-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {categories.map((category, index) => {
            const Icon = category.icon;
            return (
              <motion.div
                key={category.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="group bg-white rounded-2xl overflow-hidden border border-gray-100 hover:shadow-xl hover:border-[#D4AF37]/30 transition-all duration-500"
              >
                <div className="relative h-52 overflow-hidden">
                  <img
                    src={category.image}
                    alt={`${category.name} - RC Awards San Diego`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 flex items-center gap-2">
                    <div className="w-10 h-10 bg-[#D4AF37] rounded-lg flex items-center justify-center">
                      <Icon size={20} className="text-[#1a365d]" />
                    </div>
                    <h3 className="text-xl font-bold text-white">{category.name}</h3>
                  </div>
                </div>
                <div className="p-6">
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">{category.description}</p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {category.items.slice(0, 4).map((item, i) => (
                      <span key={i} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                        {item}
                      </span>
                    ))}
                    {category.items.length > 4 && (
                      <span className="text-xs bg-[#D4AF37]/10 text-[#1a365d] px-2 py-1 rounded font-medium">
                        +{category.items.length - 4} more
                      </span>
                    )}
                  </div>
                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <span className="text-sm text-gray-500">
                      Starting at <span className="text-[#1a365d] font-semibold">${category.startingPrice}</span>
                    </span>
                    <a
                      href="https://www.rcawards.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-[#D4AF37] font-medium hover:text-[#c9a432] transition-colors flex items-center gap-1"
                    >
                      Shop <ArrowUpRight size={14} />
                    </a>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Features Section */}
      <section className="bg-gray-50 py-20">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl text-[#1a365d] mb-4 font-bold">All Products Include</h2>
            <p className="text-gray-600 max-w-lg mx-auto">
              Premium quality and exceptional service on every order
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-6 text-center border border-gray-100"
              >
                <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Check size={20} className="text-[#D4AF37]" />
                </div>
                <h3 className="font-semibold text-[#1a365d] mb-1">{feature.title}</h3>
                <p className="text-sm text-gray-500">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services List */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <p className="text-xs uppercase tracking-widest text-[#D4AF37] mb-3 font-semibold">Our Services</p>
              <h2 className="text-3xl text-[#1a365d] mb-6 font-bold">Complete Recognition Solutions</h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                We offer a full range of services to meet all your recognition and promotional product needs. From design to delivery, we handle everything.
              </p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  'Custom Engraving',
                  'Embroidery Services',
                  'Screen Printing',
                  'Sublimation',
                  'Rush Services',
                  'Free Local Delivery',
                  'Design Assistance',
                  'Bulk Discounts'
                ].map((service, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Check size={16} className="text-[#D4AF37]" />
                    <span className="text-sm text-gray-600">{service}</span>
                  </div>
                ))}
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-4"
            >
              <img 
                src="https://images.unsplash.com/photo-1706374503210-5f200b0b315b?crop=entropy&cs=srgb&fm=jpg&q=85&w=400" 
                alt="Sports medals" 
                className="rounded-xl shadow-lg w-full h-48 object-cover"
              />
              <img 
                src="https://images.unsplash.com/photo-1654801296468-bd3aed7e9b5a?crop=entropy&cs=srgb&fm=jpg&q=85&w=400" 
                alt="Crystal award" 
                className="rounded-xl shadow-lg w-full h-48 object-cover mt-8"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#1a365d] py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl text-white mb-4 font-bold">Ready to Get Started?</h2>
            <p className="text-white/70 mb-8 max-w-lg mx-auto">
              Visit our online store for the full product selection, or contact us for a custom quote on your next project.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href="https://www.rcawards.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white text-[#1a365d] px-7 py-3.5 rounded-full text-sm font-medium hover:bg-gray-100 transition-colors inline-flex items-center justify-center gap-2"
              >
                Shop All Products
                <ArrowUpRight size={14} />
              </a>
              <Link
                to="/contact"
                className="bg-[#D4AF37] text-[#1a365d] px-7 py-3.5 rounded-full text-sm font-semibold hover:bg-[#c9a432] transition-colors inline-flex items-center justify-center gap-2"
              >
                Get a Free Quote
                <ArrowRight size={14} />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ProductsPage;
